import React from 'react';
import { connect } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import PropTypes from 'prop-types';
import cx from 'classnames';

// Multibrand-UI Components
import Col from '@zg-rentals/multibrand-ui/lib/Col';
import Row from '@zg-rentals/multibrand-ui/lib/Row';
import StyledContainer from '@zg-rentals/multibrand-ui/lib/StyledContainer';
import Text from '@zg-rentals/multibrand-ui/lib/Text';

// TODO: update mui Row, Col to use zrs-comet
import { Container } from '@zillow/zrs-comet';

// Components
import HeadWrapper from 'app/shared/components/HeadWrapper';

// Containers
import AdSpendPage from 'app/shared/containers/AdSpendPage';
import FunnelPage from 'app/shared/containers/FunnelPage';
import OverviewPage from 'app/shared/containers/OverviewPage';
import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';
import PermissionCheck from 'app/shared/components/PermissionCheck';
import AudiencePage from 'app/shared/containers/AudiencePage';
import CompetitionPage from 'app/shared/containers/CompetitionPage';

// Constants
import { ANALYTICS_PAGE } from 'app/shared/constants/RouteConstants';

// Selectors
import { RoutingSelectors, UserSelectors, AppSelectors } from 'app/shared/selectors';

import pathsManifest from 'app/shared/pathsManifest';

// Stylesheets
import './style.scss';

const DEFAULT_PAGE_TITLE = 'Analytics';

const paths = pathsManifest.getAllPaths();

const propTypes = {
    router: PropTypes.object.isRequired,
};

class AnalyticsPage extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            pageTitle: DEFAULT_PAGE_TITLE,
        };

        this.loadTab = this.loadTab.bind(this);
        this.getActiveTab = this.getActiveTab.bind(this);
        this.getSectionUrl = this.getSectionUrl.bind(this);
        this.renderChildRoutes = this.renderChildRoutes.bind(this);
        this.renderContent = this.renderContent.bind(this);
    }

    getInitialState() {
        return {
            pageTitle: DEFAULT_PAGE_TITLE,
        };
    }

    loadTab(tab) {
        const baseDetailsUrl = paths.analyticsPage;
        const url = `${baseDetailsUrl}/${tab}`;

        return window.router.transitionTo(url);
    }

    getActiveTab() {
        const { router } = this.props;

        // Expecting path to be [baseUrl]/rental-manager/analytics/*something*
        const tabName = router.location.pathname.split('/')[3];
        switch (tabName) {
            case paths.analyticsOverview:
                return paths.analyticsOverview;
            case paths.analyticsFunnel:
                return paths.analyticsFunnel;
            case paths.analyticsAdSpend:
                return paths.analyticsAdSpend;
            case paths.analyticsAudience:
                return paths.analyticsAudience;
            case paths.analyticsCompetition:
                return paths.analyticsCompetition;
            default:
                return paths.analyticsOverview;
        }
    }

    getSectionUrl(section) {
        const baseDetailsUrl = paths.analyticsPage;
        return `${baseDetailsUrl}/${section}([?].*)?`;
    }

    /**
     * Child Routes
     *
     * parent path: /rental-manager/analytics
     * child paths: /{section}
     *
     * ex: /rental-manager/analytics/overview
     */
    renderChildRoutes() {
        // child paths
        const childPathOverview = `${this.getSectionUrl(paths.analyticsOverview)}`;
        const childPathProperties = `${this.getSectionUrl(paths.analyticsFunnel)}`;
        const childPathAdSpend = `${this.getSectionUrl(paths.analyticsAdSpend)}`;
        const childPathAudience = `${this.getSectionUrl(paths.analyticsAudience)}`;
        const childPathCompetition = `${this.getSectionUrl(paths.analyticsCompetition)}`;

        return (
            <StyledContainer className="Analytics-partial-wrapper">
                <Switch>
                    {/* /overview */}
                    <Route render={() => <OverviewPage />} path={childPathOverview} />
                    {/* /properties */}
                    <Route render={() => <FunnelPage />} path={childPathProperties} />
                    {/* /adspend */}
                    <Route render={props => <AdSpendPage location={props.location} />} path={childPathAdSpend} />
                    {/* /audience */}
                    <Route render={() => <AudiencePage />} path={childPathAudience} />
                    {/* /competition */}
                    <Route render={() => <CompetitionPage />} path={childPathCompetition} />
                </Switch>
            </StyledContainer>
        );
    }

    renderContent() {
        const activeTab = this.getActiveTab();
        const { cookieEnabledServices } = this.props;
        const showRenterInform = cookieEnabledServices.ZG_RENTER_INFORM_ENABLED === true;

        return (
            <Container className="AnalyticsPage-container">
                <StyledContainer borderFull={true} borderRounded={true} className="AnalyticsPage-card">
                    <Text className="AnalyticsPage-heading" type="h1">
                        {this.state.pageTitle}
                    </Text>
                    {/* @TODO Refactor in reusable tab switcher */}
                    <StyledContainer borderBottom={true} className="AnalyticsPage-SubNav">
                        <Row noPadding={true}>
                            <Col>
                                <ul className="AnalyticsPage-SubNav-list">
                                    <li
                                        className={cx('TabMenuItem-links', {
                                            'TabMenuItem-active': activeTab === 'overview',
                                        })}
                                        onClick={() => this.loadTab(paths.analyticsOverview)}
                                    >
                                        <Text type="body" className={cx({ 'Utils-primary': activeTab === 'overview' })}>
                                            Overview
                                        </Text>
                                    </li>
                                    <li
                                        className={cx('TabMenuItem-links', {
                                            'TabMenuItem-active': activeTab === 'funnel',
                                        })}
                                        onClick={() => this.loadTab(paths.analyticsFunnel)}
                                    >
                                        <Text type="body" className={cx({ 'Utils-primary': activeTab === 'funnel' })}>
                                            Funnel
                                        </Text>
                                    </li>
                                    <li
                                        className={cx('TabMenuItem-links', {
                                            'TabMenuItem-active': activeTab === 'adspend',
                                        })}
                                        onClick={() => this.loadTab(paths.analyticsAdSpend)}
                                    >
                                        <Text type="body" className={cx({ 'Utils-primary': activeTab === 'adspend' })}>
                                            Ad Spend
                                        </Text>
                                    </li>
                                    {showRenterInform && (
                                        <li
                                            className={cx('TabMenuItem-links', {
                                                'TabMenuItem-active': activeTab === 'audience',
                                            })}
                                            onClick={() => this.loadTab(paths.analyticsAudience)}
                                        >
                                            <Text
                                                type="body"
                                                className={cx({ 'Utils-primary': activeTab === 'audience' })}
                                            >
                                                Audience
                                            </Text>
                                        </li>
                                    )}
                                    {showRenterInform && (
                                        <li
                                            className={cx('TabMenuItem-links', {
                                                'TabMenuItem-active': activeTab === 'competition',
                                            })}
                                            onClick={() => this.loadTab(paths.analyticsCompetition)}
                                        >
                                            <Text
                                                type="body"
                                                className={cx({ 'Utils-primary': activeTab === 'competition' })}
                                            >
                                                Competition
                                            </Text>
                                        </li>
                                    )}
                                </ul>
                            </Col>
                        </Row>
                    </StyledContainer>
                    {this.renderChildRoutes()}
                </StyledContainer>
            </Container>
        );
    }

    render() {
        return (
            <AuthCheckWrapper requireStrongAuthentication={false}>
                <PermissionCheck route={ANALYTICS_PAGE}>
                    <div className="AnalyticsPage">
                        <HeadWrapper title={this.state.pageTitle} />
                        {this.renderContent()}
                    </div>
                </PermissionCheck>
            </AuthCheckWrapper>
        );
    }
}

const mapStateToProps = state => ({
    user: UserSelectors.makeUser(state),
    router: RoutingSelectors.makeRouting(state),
    cookieEnabledServices: AppSelectors.makeCookieEnabledServices(state),
});

AnalyticsPage.propTypes = propTypes;

export { AnalyticsPage };
export default connect(mapStateToProps)(AnalyticsPage);
